import { useGetPlayerSession } from '@/api/User/useGetPlayerSession';
import { useProfiles } from '@/api/User/useProfiles';
import { MobileBaselineSessionTable } from '@/components/Baseline/SessionTable/MobileBaselineSessionTable';
import Loader from '@/components/MLS/Loader/Loader';
import { NewPageHeading } from '@/components/MLS/NewPageHeading/NewPageHeading';
import { isNil } from 'lodash';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

export const BaselineSessionData = ({isTrainer = false}: {isTrainer?: boolean}) => {
  const navigate = useNavigate();
  const {sessionId} = useParams();
  const [searchParams] = useSearchParams();
  const {currentProfile} = useProfiles();

  const siteId = isTrainer ? Number(searchParams.get('mboSiteId')) : Number(currentProfile?.mboDetails?.siteId);

  const {data: sessionData, isLoading} = useGetPlayerSession({
    clientId: isTrainer ? searchParams.get('mboClientId') : currentProfile?.mboDetails?.clientId,
    sessionId: sessionId,
    siteId,
  });

  const {
    startTime,
    isBaselineSession,
    baselineLevel = '',
    totalBallsFired,
    exercises,
    sessionName = '',
    isV3Session,
  } = sessionData || {};

  const startDate = startTime ? moment(startTime).format('MMM DD, YYYY') : undefined;

  let sessionDisplayText = `${baselineLevel} Baseline - ${startDate}`;
  if (isV3Session) {
    // Determine if this is a retest session based on sessionName format
    // First check if session name follows "num-num" pattern, then check if it doesn't include '-0'
    const hasNumNumPattern = sessionName && /^\d+-\d+/.test(sessionName);
    const isRetestSession = hasNumNumPattern && !sessionName.includes('-0');
    sessionDisplayText = isRetestSession
      ? `Level ${baselineLevel} Baseline Retest - ${startDate}`
      : `Level ${baselineLevel} Baseline - ${startDate}`;
  }

  const sessionSummary = sessionData?.sessionSummary;
  const scoreToShow = !isNil(sessionSummary?.tocaScore) ? Math.round(sessionSummary?.tocaScore) : !isNil(sessionSummary?.score) ? Math.round(sessionSummary?.score) : null;

  return (
    <>
      <Helmet>
        <title>MyTOCA | Baseline Session Data</title>
      </Helmet>

      {isLoading ? (
        <div className="flex min-h-screen items-center justify-center">
          <Loader text="Loading..." />
        </div>
      ) : (
        <div className="grow flex flex-col exceed-global-padding">
          <div className="pb-4 xs:px-6">
            <div className="rounded overflow-hidden">
              <div className="text-center">
                <NewPageHeading text="Baseline Session Data" onClickOverride={() => navigate(-1)} />
              </div>
              {isBaselineSession ? (
                <div className="text-lg font-medium uppercase">
                  <p className="p-3 text-center bg-mls-grey border border-mls-grey-dark rounded-t-md font-semibold">
                    {sessionDisplayText}
                  </p>
                  <div className="p-4 bg-mls-grey flex items-center justify-around border-l border-r border-mls-grey-dark">
                    <div className="flex flex-col gap-2 items-center justify-center">
                      <div className="p-3 rounded-full grid place-content-center bg-mls-baseline text-white w-16 h-16">
                        <p className="font-teko font-bold text-2xl " data-test-id="total-balls-fired">
                          {totalBallsFired}
                        </p>
                      </div>
                      <p>Balls</p>
                    </div>
                    <div className="flex flex-col gap-2 items-center justify-center">
                      <div className="p-3 rounded-full grid place-content-center bg-mls-baseline text-white w-16 h-16">
                        <p className="font-teko font-bold text-2xl" data-test-id="total-sets">
                          {exercises?.length}
                        </p>
                      </div>
                      <p>Sets</p>
                    </div>
                    {!isNil(scoreToShow) && scoreToShow > 0 && (
                      <div className="flex flex-col gap-2 items-center justify-center">
                        <div className="p-3 rounded-full grid place-content-center bg-mls-baseline text-white w-16 h-16">
                          <p className="font-teko font-bold text-2xl" data-test-id="session-score">
                            {scoreToShow}
                          </p>
                        </div>
                        <p>Score</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid place-content-center">
                  <p className="text-sm text-grey-xdark text-center p-4">Unable to load session data</p>
                </div>
              )}
              {isBaselineSession && (
                <MobileBaselineSessionTable
                  exercises={exercises}
                  baselineLevel={baselineLevel}
                  isMls={true}
                  isV3Session={isV3Session}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
